import React, { useEffect, useState } from 'react';
import {
  dateFormat,
  PaperProps,
  requiredField,
  dateFnsFormat,
  characterLimit,
  booleanOptions,
  adminManagementLinkSuccess,
} from 'constants';
import {
  onSave,
  currentDate,
  refinePayload,
  fieldValidation,
  useNotification,
  getDialogHeading,
  dropDownValidation,
  showBackdropLoader,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import moment from 'moment';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  putAdminLink,
  postAdminLink,
} from 'layouts/userLinks/services/linksApi';
import { GridForm } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';

const adminManagementSchema = yup.object().shape({
  name: fieldValidation(characterLimit),
  description: fieldValidation(characterLimit),
  url: fieldValidation(characterLimit),
  active: dropDownValidation,
  // activeUntil: yup.date().required().typeError(requiredField),
});

const AdminManagementDialog = (props) => {
  const [loadingButton, setLoadingButton] = useState(false);
  const dispatch = useDispatch();
  const { setOpenNotification, setNotificationMessage, NotificationPopup } =
    useNotification();
  const {
    open,
    setOpen,
    isNewLink,
    isFormLoading,
    adminLinkForm,
    setAdminLinkForm,
    fetchAdminAllLinks,
  } = props;
  const { name, description, url, active, id, activeUntil } =
    adminLinkForm || {};

  const defaultValue = {
    name: name || '',
    description: description || '',
    url: url || '',
    active: active || '',
    activeUntil: activeUntil
      ? format(new Date(activeUntil), dateFnsFormat)
      : '',
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(adminManagementSchema),
  });
  const { handleSubmit, reset, control, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.name,
    defaultValue.description,
    defaultValue.url,
    defaultValue.active,
    defaultValue.activeUntil,
  ]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
    setAdminLinkForm(null);
  };

  const onSubmit = (data) => {
    const { name, description, url, active, activeUntil } = data;
    const rawPayload = {
      name,
      description,
      url,
      active,
      activeUntil: activeUntil ? moment(activeUntil).format(dateFormat) : null,
      clearActiveUntil: activeUntil ? false : true,
    };
    const payload = refinePayload(rawPayload);

    onSave(
      adminManagementLinkSuccess,
      isNewLink,
      postAdminLink,
      putAdminLink,
      payload,
      id,
      fetchAdminAllLinks,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton,
      loadingButton
    );
  };

  return (
    <>
      {showBackdropLoader(isFormLoading)}
      <Dialog open={open} PaperProps={PaperProps}>
        {getDialogHeading('Admin Link', handleClose)}
        <GridForm container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormInputText
              type="text"
              name="name"
              label="Name"
              control={control}
              errors={!!errors.name}
              helperText={errors?.name?.message}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInputText
              type="text"
              control={control}
              name="description"
              label="Description"
              errors={!!errors.description}
              helperText={errors?.description?.message}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInputText
              name="url"
              label="URL"
              type="text"
              control={control}
              errors={!!errors.url}
              helperText={errors?.url?.message}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInputDropdown
              type="text"
              name="active"
              label="Active"
              control={control}
              options={booleanOptions}
              errors={!!errors.active}
              helperText={errors?.active?.message}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInputDate
              control={control}
              name="activeUntil"
              label="Active Until"
              value={activeUntil}
              minDate={currentDate}
              errors={!!errors.activeUntil}
              helperText={errors?.activeUntil?.message}
            />
          </Grid>
          {renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            loadingButton
          )}
        </GridForm>
      </Dialog>
      <NotificationPopup />
    </>
  );
};

export default AdminManagementDialog;
